import { CONFIG, tierName, hitRatePercent } from '@h2h/shared';
import { AppShell, Content } from '../../components/ui/AppShell';
import { DesktopColumns } from '../../components/ui/DesktopColumns';
import { Placeholder } from '../../components/ui/Placeholder';
import { Skeleton } from '../../components/ui/bits';
import { TierLadder } from '../../components/rounds/RoundResultsHero';
import { DivisionsHelp } from '../../components/standings/DivisionsHelpSheet';
import { Bell, Info, Settings, User as UserIcon } from '../../components/ui/icons';
import { useHaptics } from '../../hooks/useHaptics';
import { cap, formatCount, ordinalSuffix } from '../../lib/format';
import { cn } from '../../lib/cn';
import { ProfileInCommon } from '../user-profile/ProfileInCommon';
import { ProfileRoundHistory } from '../user-profile/ProfileRoundHistory';
import { ProfileSheets } from '../user-profile/ProfileSheets';
import { AchievementBadge } from '../profile/AchievementBadge';
import { Section } from '../profile/Section';
import { StandingCard } from '../profile/StandingCard';
import { StatTile } from '../profile/StatTile';
import { StatGrid } from '../profile/StatGrid';
import { profilePanel } from '../profile/tokens';
import { PlayerProfileHero } from './PlayerProfileHero';
import { PlayerRivalrySection } from './PlayerRivalrySection';
import { usePlayerProfileScreen } from './usePlayerProfileScreen';

/** One profile screen for you and every other player — league-scoped via the switcher. */
export default function PlayerProfileScreen() {
  const { impact } = useHaptics();
  const vm = usePlayerProfileScreen();

  if (vm.userLoading) {
    return (
      <AppShell surfaceClassName="bg-wash md:bg-canvas">
        <Content>
          <Placeholder Icon={UserIcon} title="Loading…" body="Fetching this profile." />
        </Content>
      </AppShell>
    );
  }

  if (!vm.targetUser) {
    return (
      <AppShell surfaceClassName="bg-wash md:bg-canvas">
        <Content>
          <Placeholder Icon={UserIcon} title="Player not found" body="This profile isn’t available." />
        </Content>
      </AppShell>
    );
  }

  const name = vm.targetUser.displayName ?? 'Player';
  const firstName = name.split(/\s+/)[0];
  const possessive = vm.isSelf ? 'Your' : `${firstName}'s`;

  const tier = vm.membership?.tier ?? 0;
  const isTopTier = vm.membership != null && vm.membership.tier >= CONFIG.TIERS.length - 1;
  const nextTier = vm.membership != null && !isTopTier ? vm.membership.tier + 1 : null;
  const hasPlayed = (vm.stats?.roundsPlayed ?? 0) > 0;
  const loading = vm.loading;

  const seasonPoints = vm.stats?.seasonPoints ?? 0;
  const rounds = vm.stats?.roundsPlayed ?? 0;
  const promotions = vm.stats?.promotions ?? 0;
  const exactHits = vm.stats?.exactScoreHits ?? 0;
  const bestRank = vm.membership?.bestRankInGroup ?? null;
  const hitRate = hitRatePercent(vm.stats);

  const picksTitle = `${possessive} round history`;

  if (vm.noLeague) {
    return (
      <AppShell surfaceClassName="bg-wash md:bg-canvas">
        <Content>
          <PageHeader vm={vm} title="Profile" />
          <Placeholder
            Icon={UserIcon}
            title={vm.isSelf ? 'Join a league first' : 'No shared leagues'}
            body={
              vm.isSelf
                ? 'Pick a competition to see your profile stats and pick history.'
                : `You and ${firstName} don’t compete in the same league yet.`
            }
          />
        </Content>
      </AppShell>
    );
  }

  return (
    <AppShell surfaceClassName="bg-wash md:bg-canvas">
      <Content width="wide">
        <PageHeader vm={vm} title={vm.isSelf ? 'Profile' : name} />

        <DesktopColumns
          railFirst
          asidePosition="left"
          asideWidth="380px"
          aside={
            <>
        <PlayerProfileHero vm={vm} name={name} tier={tier} loading={loading} />

        {!vm.isSelf && !vm.isBlocked && (
          <PlayerRivalrySection
            name={name}
            target={vm.targetUser}
            me={vm.me}
            myWins={vm.myWins}
            theirWins={vm.theirWins}
            draws={vm.draws}
            seriesLength={vm.series.length}
            hasRivalry={vm.hasRivalry}
          />
        )}
            </>
          }
          main={
            <>
        {vm.notInLeague ? (
          <Section eyebrow={vm.leagueName} title={`${possessive} standing`}>
            <Placeholder
              Icon={UserIcon}
              title="Not in this league"
              body={
                vm.isSelf
                  ? `You haven’t joined ${vm.leagueName} yet. Switch league or enter from the lobby.`
                  : `${firstName} hasn’t joined ${vm.leagueName}. Try another league you share.`
              }
            />
          </Section>
        ) : (
          <>
            <Section
              eyebrow={vm.leagueName}
              title={`${possessive} standing`}
              aside={
                loading ? (
                  <Skeleton className="h-3 w-20" />
                ) : (
                  <span className="animate-fade-in text-[12px] font-medium text-ink2">
                    {hasPlayed ? `${rounds} ${rounds === 1 ? 'round' : 'rounds'} played` : 'Not started'}
                  </span>
                )
              }
            >
              <StandingCard
                loading={loading}
                tier={tier}
                tierLabel={cap(tierName(tier))}
                bestRank={bestRank}
                isTopTier={isTopTier}
                nextTier={nextTier}
              />
            </Section>

            {vm.showPicks && (
              <Section
                title={picksTitle}
                aside={
                  <span className="text-[12px] font-medium text-ink2">
                    {vm.roundHistory.length > 0
                      ? `${vm.roundHistory.length} entered`
                      : vm.isSelf
                        ? 'Your picks by round'
                        : 'Reveal at kickoff'}
                  </span>
                }
              >
                <ProfileRoundHistory
                  name={name}
                  loading={vm.picksLoading}
                  error={vm.picksError}
                  detail={vm.picks}
                  isSelf={vm.isSelf}
                  seasonResults={vm.roundHistory}
                  onSelectRound={(rid) => {
                    impact('light');
                    vm.setSelectedRoundId(rid);
                  }}
                />
              </Section>
            )}

            <Section
              title={`${possessive} record`}
              aside={<span className="text-[12px] font-medium text-ink2">in {vm.leagueName}</span>}
            >
              <StatGrid>
                <StatTile
                  loading={loading}
                  value={formatCount(seasonPoints)}
                  label="Season pts"
                  delta={vm.isSelf ? undefined : seasonPoints - vm.myStatValues.seasonPoints}
                  formatDelta={formatCount}
                  inset
                />
                <StatTile loading={loading} value={String(rounds)} label="Rounds" inset />
                <StatTile loading={loading} value={String(promotions)} label="Promotions" accent={promotions > 0} inset />
                <StatTile
                  loading={loading}
                  value={bestRank ? `${bestRank}${ordinalSuffix(bestRank)}` : '—'}
                  label="Best finish"
                  inset
                />
                <StatTile loading={loading} value={String(exactHits)} label="Exact scores" inset />
                <StatTile
                  loading={loading}
                  value={hitRate != null ? `${hitRate}%` : '—'}
                  label="Hit rate"
                  delta={
                    vm.isSelf || hitRate == null || vm.myStatValues.hitRate == null
                      ? undefined
                      : hitRate - vm.myStatValues.hitRate
                  }
                  inset
                />
              </StatGrid>
            </Section>

            <Section title="Division ladder" aside={<DivisionsHelp tierIndex={tier} variant="icon" label="How it works" />}>
              {loading ? (
                <Skeleton className="h-[68px] w-full" rounded="rounded-2xl" />
              ) : (
                <div className="animate-fade-in">
                  <TierLadder currentTierIndex={tier} label={`${possessive} tier`} />
                </div>
              )}
              {nextTier != null && (
                <div className="mt-4 flex items-start gap-[9px] px-1">
                  <Info width={15} height={15} strokeWidth={2} className="mt-[1px] shrink-0 text-ink3" />
                  <span className="text-[12px] font-medium leading-[1.45] text-ink2">
                    Finish top {CONFIG.PROMOTE_COUNT} in your division to{' '}
                    <span className="font-bold text-ink">promote to {cap(tierName(nextTier))}</span>. The ladder resets
                    every round — a fresh, winnable race.
                  </span>
                </div>
              )}
            </Section>

            <Section
              title="Achievements"
              aside={
                <span className="text-[12px] font-bold text-ink2">
                  {vm.earnedCount} of {vm.achievements.length}
                </span>
              }
            >
              <div className={cn('p-4', profilePanel)}>
                <div className="grid grid-cols-4 gap-3">
                {vm.achievements.map((a) => (
                  <AchievementBadge key={a.id} id={a.id} label={a.label} earned={a.earned} />
                ))}
                </div>
              </div>
            </Section>
          </>
        )}

        {!vm.isSelf && (vm.sharedMinis.length > 0 || vm.mutualIds.length > 0) && (
          <ProfileInCommon
            sharedMinis={vm.sharedMinis}
            mutualIds={vm.mutualIds}
            mutualUsers={vm.mutualUsers}
            onOpenMini={(miniLeagueId) => vm.navigate(`/leagues/${miniLeagueId}`)}
          />
        )}
            </>
          }
        />
      </Content>

      {!vm.isSelf && (
        <ProfileSheets
          name={name}
          isBlocked={vm.isBlocked}
          menuOpen={vm.menuOpen}
          reportOpen={vm.reportOpen}
          blockOpen={vm.blockOpen}
          reportPending={vm.reportUser.isPending}
          blockPending={vm.blockUser.isPending}
          onCloseMenu={() => vm.setMenuOpen(false)}
          onCloseReport={() => vm.setReportOpen(false)}
          onCloseBlock={() => vm.setBlockOpen(false)}
          onOpenReport={() => {
            impact('light');
            vm.setMenuOpen(false);
            vm.setReportOpen(true);
          }}
          onReport={(reason) => {
            impact('light');
            vm.onReport(reason);
          }}
          onBlockOrUnblock={() => {
            impact('light');
            vm.setMenuOpen(false);
            if (vm.isBlocked) vm.onUnblock();
            else vm.setBlockOpen(true);
          }}
          onConfirmBlock={() => {
            impact('medium');
            vm.onConfirmBlock();
          }}
        />
      )}
    </AppShell>
  );
}

function PageHeader({ vm, title }: { vm: ReturnType<typeof usePlayerProfileScreen>; title: string }) {
  const navigate = vm.navigate;
  if (vm.isPublicRoute) return null;

  return (
    <div className="flex items-center justify-between pt-2 md:pt-5">
      <h1 className="text-[26px] font-extrabold tracking-title text-ink">{title}</h1>
      {vm.isSelf && (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate(vm.leagueId ? `/play/${vm.leagueId}/notifications` : '/notifications')}
            aria-label="Notifications"
            className="tap relative grid h-10 w-10 place-items-center rounded-full border border-black/[0.08] bg-card text-ink shadow-soft"
          >
            <Bell width={18} height={18} strokeWidth={2} />
            {vm.unread > 0 && (
              <span className="absolute right-[7px] top-[7px] h-[8px] w-[8px] rounded-full border-[1.5px] border-white bg-accent" />
            )}
          </button>
          <button
            type="button"
            onClick={() => navigate('/settings')}
            aria-label="Settings"
            className="tap grid h-10 w-10 place-items-center rounded-full border border-black/[0.08] bg-card text-ink shadow-soft"
          >
            <Settings width={18} height={18} strokeWidth={2} />
          </button>
        </div>
      )}
    </div>
  );
}
